import { useEffect, useMemo, useState } from "react";
import type { AppSummary } from "../types/apps";
import { useApps } from "../hooks/useApps";

interface AppPickerProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (appName: string) => void;
  creating?: boolean;
  requiredApp?: string;
  browserSessionsOnly?: boolean;
}

export default function AppPicker({
  visible,
  onClose,
  onSelect,
  creating = false,
  requiredApp,
  browserSessionsOnly = false,
}: AppPickerProps) {
  const { apps: allApps, loading, error } = useApps();
  const apps = useMemo(() => allApps.filter(app =>
    (!requiredApp || app.name === requiredApp) &&
    (!browserSessionsOnly || app.name === "chrome" || app.name === "firefox")), [allApps, requiredApp, browserSessionsOnly]);
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [selectedApp, setSelectedApp] = useState<string | null>(null);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(apps.map((a) => a.category))).sort();
    return ["all", ...cats];
  }, [apps]);

  const filtered = useMemo(
    () =>
      activeCategory === "all"
        ? apps
        : apps.filter((a) => a.category === activeCategory),
    [apps, activeCategory],
  );

  useEffect(() => {
    if (!visible) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  const handleCreate = () => {
    if (!selectedApp || creating) return;
    if (requiredApp && selectedApp !== requiredApp) return;
    if (!apps.some(app => app.name === selectedApp)) return;
    onSelect(selectedApp);
  };

  const toggleApp = (name: string) => {
    setSelectedApp((prev) => (prev === name ? null : name));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Select an application"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "#111827",
          borderRadius: 12,
          border: "1px solid #374151",
          width: "min(600px, 95vw)",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #374151",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 600, color: "#f3f4f6" }}>
            New Session
          </span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              color: "#9ca3af",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Category tabs */}
        <div
          style={{
            display: "flex",
            gap: 6,
            padding: "12px 20px 0",
            overflowX: "auto",
            flexShrink: 0,
          }}
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                background: activeCategory === cat ? "#2563eb" : "transparent",
                color: activeCategory === cat ? "#fff" : "#9ca3af",
                border:
                  activeCategory === cat ? "none" : "1px solid #374151",
                borderRadius: 6,
                padding: "4px 12px",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
                textTransform: "capitalize",
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* App grid */}
        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          {loading && (
            <div style={{ color: "#9ca3af", textAlign: "center", padding: 32 }}>
              Loading apps…
            </div>
          )}
          {!loading && error !== null && (
            <div style={{ color: "#f87171", textAlign: "center", padding: 32 }}>
              Failed to load apps
            </div>
          )}
          {!loading && error === null && filtered.length === 0 && (
            <div style={{ color: "#9ca3af", textAlign: "center", padding: 32 }}>
              No apps in this category
            </div>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: 10,
            }}
          >
            {filtered.map((app) => (
              <AppCard
                key={app.name}
                app={app}
                selected={selectedApp === app.name}
                onClick={() => toggleApp(app.name)}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid #374151",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "1px solid #374151",
              color: "#9ca3af",
              borderRadius: 6,
              padding: "8px 16px",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={selectedApp === null || creating}
            style={{
              background:
                selectedApp === null || creating ? "#374151" : "#2563eb",
              color: selectedApp === null || creating ? "#6b7280" : "#fff",
              border: "none",
              borderRadius: 6,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 500,
              cursor:
                selectedApp === null || creating ? "not-allowed" : "pointer",
            }}
          >
            {creating ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────

interface AppCardProps {
  app: AppSummary;
  selected: boolean;
  onClick: () => void;
}

function AppCard({ app, selected, onClick }: AppCardProps) {
  const memGb =
    app.resources.mem_mib >= 1024
      ? `${app.resources.mem_mib / 1024}GB`
      : `${app.resources.mem_mib}MB`;

  return (
    <button
      onClick={onClick}
      aria-pressed={selected}
      style={{
        background: selected ? "#1d4ed8" : "#1f2937",
        border: selected ? "2px solid #3b82f6" : "2px solid transparent",
        borderRadius: 8,
        padding: "12px 10px",
        cursor: "pointer",
        textAlign: "center",
        color: "#e5e7eb",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
      }}
    >
      <AppIcon app={app} />
      <span
        style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3, color: "#f3f4f6" }}
      >
        {app.display_name}
      </span>
      <span style={{ fontSize: 11, color: "#9ca3af" }}>
        RAM: {memGb} · vCPU: {app.resources.vcpu}
      </span>
    </button>
  );
}

function AppIcon({ app }: { app: AppSummary }) {
  const [imgError, setImgError] = useState(false);
  const letter = app.display_name.charAt(0).toUpperCase();

  if (!imgError) {
    return (
      <img
        src={`/api/v1/apps/${encodeURIComponent(app.name)}/icon.png`}
        alt={app.display_name}
        onError={() => setImgError(true)}
        style={{
          width: 40,
          height: 40,
          objectFit: "contain",
          borderRadius: 6,
        }}
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      style={{
        width: 40,
        height: 40,
        borderRadius: 8,
        background: "#374151",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 20,
        fontWeight: 700,
        color: "#9ca3af",
      }}
    >
      {letter}
    </div>
  );
}
